import { signOut } from "firebase/auth";
import { getToken } from "firebase/app-check";

import { appCheck, auth } from "../firebase.config";

const BASE = (import.meta.env.VITE_API_BASE_URL || "") + "/api/v1";

async function authedHeaders(forceFresh = false): Promise<HeadersInit> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const user = auth.currentUser;
  if (user) headers["Authorization"] = `Bearer ${await user.getIdToken(forceFresh)}`;
  if (appCheck) {
    try {
      const t = await getToken(appCheck);
      headers["X-Firebase-AppCheck"] = t.token;
    } catch {
      // App Check token unavailable — let server enforce its policy
    }
  }
  return headers;
}

export interface ApiInit extends RequestInit {
  /** Override the per-request timeout. Defaults to 30s; long-running admin
   *  calls (seed, export) should pass a much larger value. */
  timeoutMs?: number;
}

async function fetchWithTimeout(url: string, init: ApiInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const handle = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") {
      throw new ApiError(
        `Request timed out after ${Math.round(timeoutMs / 1000)}s. The server may be cold-starting; try again.`,
        0, "timeout",
      );
    }
    // Network/CORS/DNS issues surface here as a TypeError("Failed to fetch")
    throw new ApiError(
      `Network error: ${(err as Error).message || "couldn't reach the server"}.`,
      0, "network",
    );
  } finally {
    clearTimeout(handle);
  }
}

export async function api<T>(path: string, init?: ApiInit): Promise<T> {
  const timeoutMs = init?.timeoutMs ?? 30_000;
  // First attempt
  let res = await fetchWithTimeout(`${BASE}${path}`, {
    ...init,
    headers: { ...(await authedHeaders(false)), ...(init?.headers || {}) },
  }, timeoutMs);

  // 401 — refresh token may be stale or revoked. Try once with a forced refresh.
  if (res.status === 401 && auth.currentUser) {
    try {
      res = await fetchWithTimeout(`${BASE}${path}`, {
        ...init,
        headers: { ...(await authedHeaders(true)), ...(init?.headers || {}) },
      }, timeoutMs);
    } catch {
      // fall through; we'll handle below
    }
    if (res.status === 401) {
      await signOut(auth);
      throw new ApiError("Session expired. Please sign in again.", 401, "unauthorized");
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({} as Record<string, unknown>));
    const requestId = res.headers.get("X-Request-Id") || undefined;
    const err = new ApiError(
      (body.message as string | undefined) || res.statusText,
      res.status,
      body.error as string | undefined,
    );
    err.requestId = requestId;
    err.errorClass = body.errorClass as string | undefined;
    err.errorMessage = body.errorMessage as string | undefined;
    err.errorLocation = body.errorLocation as string | undefined;
    err.tracebackLines = body.traceback as string[] | undefined;
    // Mirror to the dev console so a 500 always leaves a breadcrumb without
    // requiring component-level error-display code on every screen.
    if (res.status >= 500) {
      // eslint-disable-next-line no-console
      console.error("[api] 5xx", path, {
        status: res.status,
        requestId,
        errorClass: err.errorClass,
        errorMessage: err.errorMessage,
        errorLocation: err.errorLocation,
        traceback: err.tracebackLines,
      });
    }
    throw err;
  }

  // 204 No Content has no body
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  requestId?: string;
  errorClass?: string;
  errorMessage?: string;
  errorLocation?: string;
  tracebackLines?: string[];
  constructor(message: string, public status: number, public code?: string) {
    super(message);
  }
}
